<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  PEmojiPicker,
  PHint,
  IconParametrMd as ParameterIcon,
  IconSmileMd as SmileIcon,
} from "@profeat/ui-kit";

import { TSelectOption } from "@profeat/ui-kit/dist/components/PSelect/types";
import { traverseHtml } from "~/views/trigger/utils/html";
import { DOMManager } from "~/views/trigger/utils/dom.manager";
import OptionSelector from "./OptionSelector.vue";

const ALLOWED_KEYS = [
  "Backspace",
  "Delete",
  "Control",
  "ArrowLeft",
  "ArrowRight",
];

const VARIABLE = {
  LENGTH: 6,
  TOKEN_VALUE: "{{  }}",
  CLASS_NAME: "variable-token",
  FILLED_CLASS_NAME: "filled-variable-token",
  ELEMENT_ROLE: "variable-placeholder",
  ELEMENT_VALUE_ATTRIB: "key",
};

const INPUT_LENGTH_LIMIT = {
  HEADER: 60,
  BODY: 1024,
  FOOTER: 60,
};

const REGEXP = {
  VARIABLE_TOKEN: /\{\{[^{}]*?}}/g,
  BOLD_FORMATTING: /\*.*?\*/g,
  ITALIC_FORMATTING: /_.*?_/g,
  STRIKETHROUGH_FORMATTING: /~.*?~/g,
  MONOSPACED_FORMATTING: /```.*?```/g,
};

type InputType = keyof typeof INPUT_LENGTH_LIMIT;
const props = defineProps<{
  value: string | null;
  genericSelectOptions: TSelectOption[]
  errorText?: string;
  variableKey?: string;
  variableType?: InputType;
}>();

const emit = defineEmits<{
  valueChanged: [string];
}>();

const isFocus = ref(false);
const wasFirstBlur = ref(false);

const inputRef = ref<HTMLDivElement>();
const textContent = ref("");
const optionSelectorOpened = ref(false);
const variableCount = ref(0);

let observer: MutationObserver | null = null;
let savedRange: Range | null = null;

const removeDelimiters = (
  text: string,
  startDelimiter: string,
  endDelimiter: string
): string => {
  const escStartDelim = startDelimiter.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const escEndDelim = endDelimiter.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const regex = new RegExp(`${escStartDelim}([\\s\\S]*?)${escEndDelim}`, "g");

  return text.replace(regex, "$1");
};

const contentLength = computed<number>(() => {
  let text = textContent.value;
  text = removeDelimiters(text, "*", "*");
  text = removeDelimiters(text, "_", "_");
  text = removeDelimiters(text, "~", "~");
  text = removeDelimiters(text, "```", "```");

  const clearText = text.length - variableCount.value * 3;
  return clearText + variableCount.value * VARIABLE.LENGTH;
});
const placeholder = computed<string>(() => {
  return contentLength.value > 0 || isFocus.value ? "" : "Текст шаблона";
});

const showErrors = computed<boolean>(() => {
  return !!props.errorText && wasFirstBlur.value;
});

const lengthLimit = computed<number>(() => {
  return props.variableType ? INPUT_LENGTH_LIMIT[props.variableType] : INPUT_LENGTH_LIMIT["BODY"];
});

const setVariableCount = () => {
  const variableTokens = inputRef.value!.querySelectorAll(
    `.${VARIABLE.CLASS_NAME}`
  );
  variableCount.value = variableTokens.length;
};

const insertEmoji = (emoji: string) => {
  const requestedLength = contentLength.value + emoji.length;
  if (requestedLength > lengthLimit.value) return;

  const selection = window.getSelection();
  let range: Range | null = null;

  if (
    savedRange &&
    inputRef.value!.contains(savedRange.commonAncestorContainer)
  ) {
    range = savedRange;
  } else if (selection && selection.rangeCount > 0) {
    const tempRange = selection.getRangeAt(0);

    range = inputRef.value!.contains(tempRange.commonAncestorContainer)
      ? tempRange
      : null;
  }

  if (!range) {
    range = document.createRange();
    range.selectNodeContents(inputRef.value!);
    range.collapse(false);
  }

  range.deleteContents();
  const emojiNode = document.createTextNode(emoji);
  range.insertNode(emojiNode);

  range.setStartAfter(emojiNode);
  range.collapse(true);
  selection?.removeAllRanges();
  selection?.addRange(range);

  inputRef.value!.normalize();

  savedRange = range;
  inputRef.value!.focus();
};

const getVariableElement = (text: string, value: string) => {
  const variableElement = document.createElement("span");
  variableElement.contentEditable = "false";
  variableElement.className = VARIABLE.CLASS_NAME;
  variableElement.style.color = "#1c7ef1"; // todo: перенести в css (не работает..)
  variableElement.innerHTML = `{${text}}`;
  variableElement.setAttribute("role", VARIABLE.ELEMENT_ROLE);
  variableElement.setAttribute(VARIABLE.ELEMENT_VALUE_ATTRIB, String(value));
  return variableElement;
};

const insertVariable = (data: TSelectOption) => {
  const requestedLength = contentLength.value + VARIABLE.LENGTH;
  if (requestedLength > lengthLimit.value) return;

  const selection = window.getSelection();
  if (selection && inputRef.value) {
    let range: Range | null = null;

    if (selection.rangeCount > 0) {
      const tempRange = selection.getRangeAt(0);
      range = inputRef.value.contains(tempRange.commonAncestorContainer)
        ? tempRange
        : null;
    }
    if (!range) {
      range = document.createRange();
      range.selectNodeContents(inputRef.value);
      range.collapse(false);
    }

    const variableElement = getVariableElement(data.text, String(data.value));

    range.deleteContents();
    range.insertNode(variableElement);

    range.setStartAfter(variableElement);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  inputRef.value?.focus();
};

const focusInput = (event: MouseEvent) => {
  event.preventDefault();

  inputRef.value!.focus();
  DOMManager.setCaretPosition(inputRef.value!, "start");
};

const saveSelection = () => {
  const selection = window.getSelection();
  if (
    inputRef.value &&
    selection?.rangeCount &&
    document.activeElement === inputRef.value
  ) {
    savedRange = selection.getRangeAt(0);
  }
};

const handleInput = (event: Event) => {
  const requestedLength = contentLength.value + VARIABLE.LENGTH;
  if (requestedLength > lengthLimit.value) event.preventDefault();

  const isEmpty = ["<br>", "<br/>"].includes(inputRef.value!.innerHTML);
  if (isEmpty) inputRef.value!.innerHTML = "";
};
const handleKeydown = (event: KeyboardEvent) => {
  if (ALLOWED_KEYS.includes(event.key) || event.metaKey || event.ctrlKey) {
    return;
  }

  if (contentLength.value >= lengthLimit.value) event.preventDefault();
};
const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();

  const pasteText = event.clipboardData?.getData("text/plain") || "";
  const selection = window.getSelection();

  if (!selection?.rangeCount || !inputRef.value) return;

  const range = selection.getRangeAt(0);
  const remainingLength = lengthLimit.value - contentLength.value;

  if (remainingLength <= 0) return;

  const textToInsert = pasteText.slice(0, remainingLength);

  range.deleteContents();
  const textNode = document.createTextNode(textToInsert);
  range.insertNode(textNode);
  inputRef.value.normalize();

  const node =
    range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer
      : textNode;
  const newCaretPosition = range.startOffset + textToInsert.length;
  DOMManager.setCaretPosition(inputRef.value, newCaretPosition, node);
};
const handleEnter = (event: KeyboardEvent) => {
  event.preventDefault();

  const selection = window.getSelection();
  if (selection?.rangeCount) {
    const range = selection.getRangeAt(0);

    range.deleteContents();

    const textNode = document.createTextNode("\n\u00A0");
    range.insertNode(textNode);

    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
};

const genericValueEncodedText = (genericValue: string) => {
  return genericValue ? `{{${genericValue}}}` : "";
};

const geTextValue = (element: HTMLDivElement): string => {
  let text = "";
  for (const el of traverseHtml(element)) {
    if (el.nodeType === Node.TEXT_NODE) {
      if (el.parentElement && el.parentElement.classList.contains("textarea")) {
        text += el.textContent;
      }
    }
    if (
      typeof el.getAttribute === "function" &&
      el.getAttribute("role") === VARIABLE.ELEMENT_ROLE
    ) {
      text +=
        genericValueEncodedText(
          el.getAttribute(VARIABLE.ELEMENT_VALUE_ATTRIB) || ""
        ) + " ";
    }
  }
  return text.trim();
};

const restoreHtmlFromValue = (text: string): string => {
  let result = text;
  const regex = /{{([^{}]*)}}/g;
  const results: RegExpExecArray[] = [];
  let m: RegExpExecArray | null;

  while ((m = regex.exec(text))) {
    results.push(m);
  }
  if (results && results.length) {
    for (const r of results) {
      const selectOption = props.genericSelectOptions.find(
        (o) => o.value === r[1]
      );
      if (selectOption) {
        const variableElement = getVariableElement(
          selectOption.text,
          String(selectOption.value)
        );
        result = result.replace(r[0], variableElement.outerHTML);
      }
    }
  }

  return result;
};

watch(
  () => props.genericSelectOptions,
  () => {
    if (props.value) {
      inputRef.value!.innerHTML = restoreHtmlFromValue(props.value);
    }
  }
);

onMounted(() => {
  document.addEventListener("selectionchange", saveSelection);

  if (inputRef.value) {
    observer = new MutationObserver(() => {
      textContent.value = geTextValue(inputRef.value!);
      const sanitizedText = DOMManager.sanitizeContent(textContent.value);
      emit("valueChanged", sanitizedText);
      setVariableCount();
    });

    observer.observe(inputRef.value, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  if (props.value) {
    inputRef.value!.innerHTML = restoreHtmlFromValue(props.value);
  }
});
onBeforeUnmount(() => {
  document.removeEventListener("selectionchange", saveSelection);

  if (observer) observer.disconnect();
});
</script>

<template>
  <div :class="['template-textarea']">
    <div :class="['textarea-wrapper', 'relative', { focus: isFocus }, { 'error-border': showErrors }]">
      <div
        ref="inputRef"
        class="textarea"
        contenteditable="true"
        dir="auto"
        @input="handleInput"
        @keydown="handleKeydown"
        @keydown.enter="handleEnter"
        @paste="handlePaste"
        @focus="isFocus = true"
        @blur="isFocus = false; wasFirstBlur = true"
      />

      <span class="textarea-placeholder" @mousedown="focusInput">
        {{ placeholder }}
      </span>
    </div>
    <div class="error-text" v-show="showErrors">
        {{ errorText }}
    </div>
    <div class="textarea-footer">
      <div class="actions">
        <PEmojiPicker :placement="'bottom-end'" @select:emoji="insertEmoji">
          <template #activator="{ setReference, trigger }">
            <PHint :placement="'bottom'">
              <template
                #activator="{ setReference: hintRef, onHover, outHover }"
              >
                <button
                  :ref="
                    (el) => {
                      setReference(el);
                      hintRef(el);
                    }
                  "
                  class="action action-emoji"
                  @click="trigger"
                  @mouseover="onHover"
                  @mouseleave="outHover"
                >
                  <SmileIcon />
                </button>
              </template>
              <template #hint> Смайлик </template>
            </PHint>
          </template>
        </PEmojiPicker>
        <PDropdown
          :model-value="optionSelectorOpened"
          :width="300"
          :close-on-self-click="false"
          :ignore-click-outside="false"
          @update:model-value="
            (e) => {
              optionSelectorOpened = e;
            }
          "
        >
          <template #activator="{ setReference, click }">
            <button
              :ref="setReference"
              class="action action-variable"
              @click="click"
            >
              <ParameterIcon />
            </button>
          </template>

          <template #default>
            <div class="content" ref="elWrap">
              <OptionSelector
                :select-options="genericSelectOptions"
                @value-changed="
                  (value) => {
                    insertVariable(value);
                    optionSelectorOpened = false;
                  }
                "
              />
            </div>
          </template>
        </PDropdown>
      </div>

      <span class="content-length">
        {{ contentLength }} / {{ lengthLimit }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.template-textarea {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.textarea-wrapper {
  width: 100%;
  height: 88px;
  padding: 8px 16px;
  border: 1px solid var(--common-rain);
  border-radius: 8px;
  overflow: hidden;
}

.textarea {
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  color: var(--common-black);
  white-space: pre-wrap;

  height: 100%;
  overflow-x: auto;
}
.textarea-placeholder {
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  color: var(--common-asphalt);

  position: absolute;
  top: 8px;
  left: 16px;
}

.textarea-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.actions {
  display: flex;
  gap: 16px;
}
.action {
  display: flex;
  align-items: center;
  justify-content: center;

  color: var(--common-asphalt);
}

.content-length {
  font-size: 12px;
  font-weight: 400;
  line-height: 100%;
  color: var(--common-asphalt);
}

.error-text {
  font-size: 12px;
  font-weight: 400;
  line-height: 100%;
  color: var(--ui-error);
}

.error-border {
  border: 1px solid var(--ui-error);
}

:deep(.p-emoji-picker-wrapper) {
  width: 20px;
}

.textarea-disabled {
  pointer-events: none;
}
.textarea-disabled .textarea-wrapper {
  background: var(--common-fog);
}
.textarea-disabled .textarea {
  color: var(--common-dull-sky);
}
.variable-token {
  color: var(--additional-blue);
}
</style>
